// Thin axios wrapper. Backend URL comes only from env, never hardcoded.
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = "studio_admin_token_v1";

export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(t) {
  if (t) sessionStorage.setItem(TOKEN_KEY, t);
  else sessionStorage.removeItem(TOKEN_KEY);
}

const http = axios.create({ baseURL: API, timeout: 20000 });
http.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Chunked upload threshold — anything bigger than this is split into 50 MB
// chunks so we stay safely below the 100 MB ingress proxy limit.
const CHUNK_THRESHOLD = 80 * 1024 * 1024; // 80 MB
const CHUNK_SIZE = 50 * 1024 * 1024; // 50 MB

function genFileId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return `f-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function singleUpload(file, onProgress) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await http.post("/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 0, // disable axios timeout for uploads — proxy handles its own
    onUploadProgress: (ev) => {
      if (onProgress && ev.total) {
        onProgress(Math.round((ev.loaded / ev.total) * 100));
      }
    },
  });
  return res.data;
}

async function chunkedUpload(file, onProgress) {
  const fileId = genFileId();
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let uploadedBytes = 0;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end);
    const fd = new FormData();
    fd.append("file", blob, `${file.name}.part${i}`);
    fd.append("fileId", fileId);
    fd.append("chunkIndex", String(i));
    fd.append("totalChunks", String(totalChunks));

    const chunkBytes = end - start;
    await http.post("/uploads/chunk", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 0,
      onUploadProgress: (ev) => {
        if (!onProgress) return;
        const totalLoaded = uploadedBytes + (ev.loaded || 0);
        onProgress(Math.min(99, Math.round((totalLoaded / file.size) * 100)));
      },
    });
    uploadedBytes += chunkBytes;
    if (onProgress) {
      onProgress(Math.min(99, Math.round((uploadedBytes / file.size) * 100)));
    }
  }

  const res = await http.post(
    "/uploads/complete",
    { fileId, filename: file.name, contentType: file.type },
    { timeout: 60000 },
  );
  if (onProgress) onProgress(100);
  return res.data;
}

const api = {
  auth: {
    login: (password) =>
      http.post("/auth/login", { password }).then((r) => r.data),
    me: () => http.get("/auth/me").then((r) => r.data),
  },
  cards: {
    list: () => http.get("/cards").then((r) => r.data),
    create: (body) => http.post("/cards", body).then((r) => r.data),
    patch: (id, body) => http.patch(`/cards/${id}`, body).then((r) => r.data),
    remove: (id) => http.delete(`/cards/${id}`).then((r) => r.data),
  },
  analytics: {
    recordVisit: () => http.post("/analytics/visit").then((r) => r.data),
    getVisits: () => http.get("/analytics/visits").then((r) => r.data),
  },
  profile: {
    get: () => http.get("/profile").then((r) => r.data),
    patch: (body) => http.patch("/profile", body).then((r) => r.data),
  },
  admin: {
    storage: () => http.get("/admin/storage").then((r) => r.data),
  },
  uploads: {
    // Auto-chunks anything bigger than CHUNK_THRESHOLD so we don't hit the
    // 100 MB nginx/Cloudflare ingress limit. Smaller files still go through
    // the single-shot endpoint for speed.
    upload: (file, onProgress) =>
      file && file.size > CHUNK_THRESHOLD
        ? chunkedUpload(file, onProgress)
        : singleUpload(file, onProgress),
  },
};

export default api;
